'use client';

import { useState, useMemo } from 'react';
import Link from 'next/link';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, Clock, ArrowRight, Search, Mail, Send } from 'lucide-react';
import { useSiteData } from '@/lib/dataCache';
import OptimizedImage from '@/components/ui/OptimizedImage';

type ApiCategory = {
  id: string;
  slug: string;
  name: string;
};

type ApiBlogPost = {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  coverImage?: string | null;
  author: string;
  category: string;
  tags: string[];
  featured: boolean;
  readTime: number;
  publishedAt?: string | null;
  createdAt: string;
};

const GRADIENTS = [
  'linear-gradient(135deg, #6C63FF 0%, #8D84FF 50%, #FF6584 100%)',
  'linear-gradient(135deg, #FF6584 0%, #FF84A2 100%)',
  'linear-gradient(135deg, #5A50F0 0%, #FF6584 100%)',
  'linear-gradient(135deg, #06b6d4 0%, #6366f1 100%)',
  'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
  'linear-gradient(135deg, #ef4444 0%, #f59e0b 100%)',
  'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
  'linear-gradient(135deg, #22c55e 0%, #06b6d4 100%)',
  'linear-gradient(135deg, #0ea5e9 0%, #6366f1 100%)',
];

const EMOJIS = ['⚡', '🔷', '✨', '🎨', '⚛️', '📰', '🧱', '♿', '🌐', '🚀', '🛠️', '💡'];

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: 'easeOut' as const },
  },
};

export default function BlogPage() {
  const [activeCategory, setActiveCategory] = useState<string>('All');
  const [query, setQuery] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [email, setEmail] = useState('');

  const posts = useSiteData<ApiBlogPost[]>('blog', '/api/blog', []);
  const categories = useSiteData<ApiCategory[]>(
    'categories-blog',
    '/api/categories?type=blog',
    [],
  );
  const BLOG_CATEGORIES: string[] = useMemo(
    () => ['All', ...categories.map((c) => c.name)],
    [categories],
  );

  const filteredPosts = useMemo(() => {
    const q = query.trim().toLowerCase();
    return posts.filter((post) => {
      const tags = (post.tags as string[]) ?? [];
      const matchesCategory =
        activeCategory === 'All' || post.category === activeCategory;
      const matchesQuery =
        q.length === 0 ||
        post.title.toLowerCase().includes(q) ||
        post.excerpt.toLowerCase().includes(q) ||
        tags.some((t) => t.toLowerCase().includes(q));
      return matchesCategory && matchesQuery;
    });
  }, [activeCategory, query, posts]);

  const featured = posts.find((p) => p.featured) ?? posts[0] ?? null;
  const listPosts = featured
    ? filteredPosts.filter((p) => p.slug !== featured.slug)
    : filteredPosts;

  const q = query.trim().toLowerCase();
  const showFeatured =
    !!featured &&
    (activeCategory === 'All' || activeCategory === featured.category) &&
    (q === '' ||
      featured.title.toLowerCase().includes(q) ||
      featured.excerpt.toLowerCase().includes(q));

  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubscribed(true);
    setEmail('');
    setTimeout(() => setSubscribed(false), 3500);
  };

  const featuredGradient = featured
    ? GRADIENTS[posts.findIndex((p) => p.slug === featured.slug) % GRADIENTS.length]
    : GRADIENTS[0];
  const featuredEmoji = featured
    ? EMOJIS[posts.findIndex((p) => p.slug === featured.slug) % EMOJIS.length]
    : EMOJIS[0];

  return (
    <motion.div
      className="blog-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Hero */}
      <section className="blog-page-hero">
        <div className="blog-page-hero-blob blog-page-hero-blob-1" />
        <div className="blog-page-hero-blob blog-page-hero-blob-2" />
        <div className="blog-page-hero-grid" />

        <div className="section-container">
          <motion.div
            className="section-heading blog-page-hero-heading"
            initial="hidden"
            animate="visible"
            variants={fadeUp}
          >
            <span className="section-label">BLOG</span>
            <h1 className="section-title blog-page-hero-title">
              Insights &amp; Articles
            </h1>
            <p className="section-subtitle">
              Tutorials, opinion pieces and deep dives on modern web
              development, design systems and product craft.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Controls */}
      <section className="blog-page-controls-section">
        <div className="section-container">
          <div className="blog-page-controls">
            <div className="blog-page-tabs">
              {BLOG_CATEGORIES.map((category) => (
                <button
                  key={category}
                  type="button"
                  onClick={() => setActiveCategory(category)}
                  className={`blog-page-tab ${
                    activeCategory === category ? 'blog-page-tab-active' : ''
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>

            <div className="blog-page-search">
              <Search size={16} className="blog-page-search-icon" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search articles, tags, topics..."
                className="blog-page-search-input"
                aria-label="Search articles"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Featured */}
      {showFeatured && featured && (
        <section className="blog-page-featured-section">
          <div className="section-container">
            <motion.article
              className="blog-page-featured"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.6 }}
            >
              <div
                className="blog-page-featured-media"
                style={{ background: featuredGradient }}
              >
                <div className="blog-page-featured-shine" />
                {featured.coverImage ? (
                  <OptimizedImage
                    src={featured.coverImage}
                    alt={featured.title}
                    className="blog-page-featured-img"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                    pictureStyle={{
                      position: 'absolute',
                      inset: 0,
                      display: 'block',
                    }}
                    loading="lazy"
                  />
                ) : (
                  <span className="blog-page-featured-emoji">{featuredEmoji}</span>
                )}
                <span className="blog-page-featured-ribbon">Featured</span>
              </div>

              <div className="blog-page-featured-body">
                <div className="blog-page-featured-tags">
                  <span className="blog-page-tag">{featured.category}</span>
                  {((featured.tags as string[]) ?? []).slice(0, 2).map((tag) => (
                    <span key={tag} className="blog-page-tag-outline">
                      #{tag}
                    </span>
                  ))}
                </div>
                <h2 className="blog-page-featured-title">{featured.title}</h2>
                <p className="blog-page-featured-excerpt">
                  {featured.excerpt}
                </p>
                <div className="blog-page-featured-meta">
                  <div className="blog-page-author">
                    <div className="blog-page-author-avatar">
                      {featured.author.charAt(0)}
                    </div>
                    <div>
                      <p className="blog-page-author-name">
                        {featured.author}
                      </p>
                      <p className="blog-page-author-date">
                        <Calendar size={12} />{' '}
                        {formatDate(featured.publishedAt || featured.createdAt)}
                        <span className="blog-page-dot" />
                        <Clock size={12} /> {featured.readTime} min read
                      </p>
                    </div>
                  </div>
                  <Link
                    href={`/blog/${featured.slug}`}
                    className="blog-page-featured-cta"
                  >
                    Read Article <ArrowRight size={16} />
                  </Link>
                </div>
              </div>
            </motion.article>
          </div>
        </section>
      )}

      {/* Grid */}
      <section className="blog-page-grid-section">
        <div className="section-container">
          <div className="blog-page-grid">
            <AnimatePresence mode="popLayout">
              {listPosts.map((post, i) => {
                const gradient = GRADIENTS[i % GRADIENTS.length];
                const emoji = EMOJIS[i % EMOJIS.length];
                const dateStr = formatDate(post.publishedAt || post.createdAt);
                return (
                  <motion.article
                    key={post.slug}
                    layout
                    className="blog-page-card"
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{
                      duration: 0.5,
                      delay: Math.min(i * 0.05, 0.3),
                    }}
                  >
                    <Link
                      href={`/blog/${post.slug}`}
                      className="blog-page-card-link"
                    >
                      <div
                        className="blog-page-card-header"
                        style={{ background: gradient }}
                      >
                        {post.coverImage ? (
                          <OptimizedImage
                            src={post.coverImage}
                            alt={post.title}
                            className="blog-page-card-img"
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                            loading="lazy"
                          />
                        ) : (
                          <>
                            <div className="blog-page-card-pattern" />
                            <span className="blog-page-card-emoji">{emoji}</span>
                          </>
                        )}
                        <span className="blog-page-card-category">
                          {post.category}
                        </span>
                      </div>

                      <div className="blog-page-card-body">
                        <h3 className="blog-page-card-title">{post.title}</h3>
                        <p className="blog-page-card-excerpt">{post.excerpt}</p>

                        <div className="blog-page-card-footer">
                          {dateStr && (
                            <>
                              <span className="blog-page-card-meta">
                                <Calendar size={12} /> {dateStr}
                              </span>
                              <span className="blog-page-dot" />
                            </>
                          )}
                          <span className="blog-page-card-meta">
                            <Clock size={12} /> {post.readTime} min read
                          </span>
                        </div>

                        <span className="blog-page-card-more">
                          Read More <ArrowRight size={14} />
                        </span>
                      </div>
                    </Link>
                  </motion.article>
                );
              })}
            </AnimatePresence>
          </div>

          {listPosts.length === 0 && !showFeatured && (
            <div className="blog-page-empty">
              <span className="blog-page-empty-emoji">📝</span>
              <p>
                {posts.length === 0
                  ? 'No articles published yet — check back soon.'
                  : 'No articles match your filters right now.'}
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Newsletter */}
      <section className="blog-page-newsletter-section">
        <div className="section-container">
          <motion.div
            className="blog-page-newsletter"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="blog-page-newsletter-glow" />
            <div className="blog-page-newsletter-body">
              <span className="blog-page-newsletter-icon">
                <Mail size={22} />
              </span>
              <h3 className="blog-page-newsletter-title">
                Get articles in your inbox
              </h3>
              <p className="blog-page-newsletter-subtitle">
                One thoughtful email a month. No spam, unsubscribe anytime.
              </p>

              <form
                className="blog-page-newsletter-form"
                onSubmit={handleSubscribe}
              >
                <input
                  type="email"
                  required
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="blog-page-newsletter-input"
                  aria-label="Email address"
                />
                <button
                  type="submit"
                  className="blog-page-newsletter-submit"
                >
                  <Send size={16} />
                  Subscribe
                </button>
              </form>

              <AnimatePresence>
                {subscribed && (
                  <motion.p
                    className="blog-page-newsletter-success"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                  >
                    Thanks! Check your inbox to confirm.
                  </motion.p>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
}
